/**
 * Co-Op Victory/Defeat Overlay
 *
 * Displays battle results for co-op encounters with star rating
 * and creature HP changes.
 */

import React from 'react';

interface CreatureHpChange {
  creatureId: string;
  name?: string;
  previousHp: number;
  newHp: number;
  maxHp: number;
  revived: boolean;
  ownerId: string;
}

interface CoOpVictoryOverlayProps {
  victory: boolean;
  stars: 0 | 1 | 2 | 3;
  creatureChanges: CreatureHpChange[];
  currentUserId: string;
  onReturnToMap: () => void;
}

export const CoOpVictoryOverlay: React.FC<CoOpVictoryOverlayProps> = ({
  victory,
  stars,
  creatureChanges,
  currentUserId,
  onReturnToMap,
}) => {
  const starDisplay = '\u2605'.repeat(stars) + '\u2606'.repeat(3 - stars);

  const myCreatures = creatureChanges.filter(c => c.ownerId === currentUserId);
  const partnerCreatures = creatureChanges.filter(c => c.ownerId !== currentUserId);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.85)',
      zIndex: 100,
      color: '#fff',
      fontFamily: 'var(--font-display, serif)',
    }}>
      <h1 style={{
        fontSize: '2.5rem',
        color: victory ? '#FFD700' : '#FF4444',
        marginBottom: '0.5rem',
      }}>
        {victory ? 'VICTORY' : 'DEFEAT'}
      </h1>

      <div style={{ fontSize: '2rem', marginBottom: '1.5rem', letterSpacing: '0.3rem' }}>
        {starDisplay}
      </div>

      {creatureChanges.length > 0 && (
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          {myCreatures.length > 0 && (
            <div>
              <h3 style={{ color: '#FFD700', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Your Team</h3>
              {myCreatures.map(c => (
                <div key={c.creatureId} style={{ fontSize: '0.85rem', marginBottom: '0.25rem', opacity: c.newHp <= 0 ? 0.5 : 1 }}>
                  {c.name || c.creatureId}: {c.previousHp} &rarr; {c.newHp} HP
                  {c.revived && ' (Revived)'}
                  {c.newHp <= 0 && ' (Dead)'}
                </div>
              ))}
            </div>
          )}
          {partnerCreatures.length > 0 && (
            <div>
              <h3 style={{ color: '#B87333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Partner's Team</h3>
              {partnerCreatures.map(c => (
                <div key={c.creatureId} style={{ fontSize: '0.85rem', marginBottom: '0.25rem', opacity: c.newHp <= 0 ? 0.5 : 1 }}>
                  {c.name || c.creatureId}: {c.previousHp} &rarr; {c.newHp} HP
                  {c.revived && ' (Revived)'}
                  {c.newHp <= 0 && ' (Dead)'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        onClick={onReturnToMap}
        style={{
          padding: '0.75rem 2rem',
          fontSize: '1.1rem',
          background: 'linear-gradient(135deg, #4a3728, #6b4f3a)',
          color: '#FFD700',
          border: '2px solid #8B6914',
          borderRadius: '8px',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Return to Map
      </button>
    </div>
  );
};
